import * as z from 'zod';
import { SpotifyTrack } from '@/services/spotifyService';

// LOR-specific schema
export const lorMetadataSchema = z.object({
  controller_type: z.enum(['AC_ONLY', 'RGB', 'MIXED'], {
    error: (issue) => issue.input === undefined ? 'Controller type is required' : 'Invalid controller type',
  }),
  channel_count_ac: z.number().min(1, 'Channel count must be at least 1').max(512, 'Channel count must be at most 512').optional(),
  software_version: z.enum(['S4', 'S5', 'S6']).optional(),
  prop_types: z.array(z.string()).max(20, 'Maximum 20 prop types allowed').default([]),
  license_level_required: z.enum(['BASIC', 'STANDARD', 'ADVANCED', 'PRO']).optional(),
  sequence_type_tags: z.array(z.enum(['TRADITIONAL', 'RGB_PLUS', 'SINGING_FACES'])).default([]),
  import_notes: z.string().max(2000, 'Import notes must be 2000 characters or less').optional(),
}).superRefine((data, ctx) => {
  // Channel count is now optional for all controller types
  // Ensure channel_count_ac is not provided when controller_type is RGB
  if (data.controller_type === 'RGB' && data.channel_count_ac) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Channel count should not be provided for RGB controller type',
      path: ['channel_count_ac']
    });
  }

  // Validate prop types
  if (data.prop_types && Array.isArray(data.prop_types)) {
    data.prop_types.forEach((propType, index) => {
      if (typeof propType !== 'string' || propType.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Prop type must be a non-empty string',
          path: ['prop_types', index]
        });
      } else if (propType.length > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Prop type must be 100 characters or less',
          path: ['prop_types', index]
        });
      }
    });
  }

  // Validate sequence type tags
  if (data.sequence_type_tags && Array.isArray(data.sequence_type_tags)) {
    const validTags = ['TRADITIONAL', 'RGB_PLUS', 'SINGING_FACES'];
    data.sequence_type_tags.forEach((tag, index) => {
      if (!validTags.includes(tag)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Invalid sequence type tag: ${tag}. Must be one of: ${validTags.join(', ')}`,
          path: ['sequence_type_tags', index]
        });
      }
    });
  }
});

export type LORMetadata = z.infer<typeof lorMetadataSchema>;

// Props-specific schema
export const sequencePropSchema = z.object({
  prop_id: z.string().uuid('Invalid prop ID'),
  quantity: z.number().min(1, 'Quantity must be at least 1').optional(),
  notes: z.string().max(500, 'Notes must be 500 characters or less').optional(),
});

export type SequenceProp = z.infer<typeof sequencePropSchema>;

export const sequenceSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  artist: z.string().min(1, 'Artist is required'),
  duration: z.number().optional(),
  description: z.string().optional(),
  software_type: z.enum(['xLights', 'Light-O-Rama', 'Vixen', 'Other']),
  holiday_type: z.enum(['Christmas', 'Halloween', 'FourthOfJuly', 'Easter', 'Thanksgiving', 'Hanukkah'], {
    error: (issue) => issue.input === undefined ? 'Holiday is required' : 'Invalid holiday type',
  }),
  file_type: z.enum(['Sequence only', 'Lyrics only', 'Sequence & Lyrics', 'Singing Face', 'Matrix Video'], {
    error: (issue) => issue.input === undefined ? 'File type is required' : 'Invalid file type',
  }),
  // New multi-genre fields
  primary_genre: z.string().optional(),
  secondary_genre: z.string().optional(),
  genres: z.array(z.string()).max(6, 'Select at most 6 genres').default([]),
  genres_overridden: z.boolean().default(false),
  price: z.number().min(0, 'Price must be 0 or greater'),
  is_free: z.boolean().default(false),
  display_id: z.string().optional(),
  album: z.string().optional(),
  album_cover_url: z.string().optional(),
  video_url: z.string().optional(),
  // LOR-specific metadata (optional, only when software_type is Light-O-Rama)
  lor_metadata: lorMetadataSchema.optional(),
  // Props selection (optional, for xLights/LOR sequences)
  props: z.array(sequencePropSchema).max(20, 'Maximum 20 props allowed').default([]),
  // Additional Spotify metadata fields (not directly in form but used by system)
  spotify_preview_url: z.string().optional(),
  spotify_explicit: z.boolean().optional(),
  spotify_duration_ms: z.number().optional(),
  spotify_popularity: z.number().optional(),
}).superRefine((data, ctx) => {
  // Ensure primary/secondary are part of genres if provided
  if (data.primary_genre && !data.genres.includes(data.primary_genre)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Primary genre must be included in selected genres',
      path: ['primary_genre']
    });
  }
  if (data.secondary_genre) {
    if (!data.genres.includes(data.secondary_genre)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Secondary genre must be included in selected genres',
        path: ['secondary_genre']
      });
    }
    if (data.primary_genre && data.secondary_genre === data.primary_genre) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Secondary genre must be different from primary genre',
        path: ['secondary_genre']
      });
    }
  }
  
  // Ensure LOR metadata is provided when software_type is Light-O-Rama
  if (data.software_type === 'Light-O-Rama' && !data.lor_metadata) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Light-O-Rama metadata is required when software type is Light-O-Rama',
      path: ['lor_metadata']
    });
  }
  
  // Ensure LOR metadata is not provided when software_type is not Light-O-Rama
  if (data.software_type !== 'Light-O-Rama' && data.lor_metadata) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Light-O-Rama metadata should not be provided for non-Light-O-Rama software types',
      path: ['lor_metadata']
    });
  }
});

export type SequenceFormData = z.infer<typeof sequenceSchema>;

export interface UploadedFile extends File {
  uploadedPath?: string;
  uploadedSize?: number;
}

export interface UploadWizardState {
  currentStep: number;
  uploadedFile: UploadedFile | null;
  selectedSpotifyTrack: SpotifyTrack | null;
  formData: Partial<SequenceFormData>;
  uploadProgress: number;
  isUploading: boolean;
}

export type WizardStep = 'file' | 'details' | 'review';

export const WIZARD_STEPS: { id: WizardStep; label: string; description: string }[] = [
  { id: 'file', label: 'Upload File', description: 'Select your sequence file' },
  { id: 'details', label: 'Add Details', description: 'Basic info, Spotify match & pricing' },
  { id: 'review', label: 'Review', description: 'Review and submit' },
];